import { FC } from 'react'
import { useFormik } from 'formik'
import { enqueueSnackbar } from 'notistack'

import { TournamentStatus } from '@img-arena/consoles-api-dde'
import { GetSports } from '@img-arena/consoles-api-streaming'
import { Typography } from '@mui/material'
import { ConsoleDialog } from '@/components/ConsoleDialog'
import { SelectBox } from '@/components/inputs/SelectBox'
import { ddeApi } from '@/service/ddeApi'
import { GolfTournamentWithFeedCount } from '@/stores/tournaments/tournaments.utils'
import { FormDialogProps } from '@/types/forms'
import { formikSaveButtonProps } from '@/utils/forms'
import { onFieldChange } from '@/utils/forms/formik'

import { UPDATE_TOURNAMENT_DATA_STATUS_DIALOG_TITLE } from './UpdateTournamentDataStatusForm.constants'

type Option = {
  label: string
  value: TournamentStatus
}

const OPTIONS: Option[] = [
  { label: 'Scheduled', value: TournamentStatus.Scheduled },
  { label: 'Test', value: TournamentStatus.Test },
  { label: 'Simulation', value: TournamentStatus.Simulation },
  { label: 'Disabled', value: TournamentStatus.Disabled }
]

export type UpdateTournamentFormValues = {
  tournamentDataStatus?: TournamentStatus
}

type UpdateTournamentDataStatusFormProps = FormDialogProps<GetSports> & {
  tournament?: GolfTournamentWithFeedCount | null
  tournamentId: GolfTournamentWithFeedCount['id']
}

const UpdateTournamentDataStatusForm: FC<UpdateTournamentDataStatusFormProps> = ({
  open,
  tournament,
  tournamentId,
  onClose,
  onSubmit
}) => {
  const formik = useFormik<UpdateTournamentFormValues>({
    initialValues: {
      tournamentDataStatus: tournament?.tournamentDataStatus
    },
    enableReinitialize: true,
    onSubmit: async ({ tournamentDataStatus }) => {
      if (!tournamentDataStatus) return

      try {
        await ddeApi.endpoints.golf.tournaments.updateStatus({
          tournamentId,
          status: tournamentDataStatus
        })

        onSubmit?.()
        onClose?.()
        enqueueSnackbar('Successfully updated tournament status', { variant: 'success' })
      } catch (error) {
        enqueueSnackbar(`Unable to change tournament status to ${tournamentDataStatus}`, {
          variant: 'error'
        })
        throw error
      }
    }
  })

  const { tournamentDataStatus } = formik.values

  return (
    <ConsoleDialog
      open={open}
      title={UPDATE_TOURNAMENT_DATA_STATUS_DIALOG_TITLE}
      onClose={() => {
        formik.resetForm()
        onClose?.()
      }}
      SaveButtonProps={{ ...formikSaveButtonProps(formik), text: 'Confirm' }}
    >
      <Typography variant="body1">Select the status for tournament {tournamentId}</Typography>
      <SelectBox
        fullWidth
        label="Status"
        value={{ label: tournamentDataStatus || '', value: tournamentDataStatus }}
        getOptionId={(val) => val.label}
        onChange={(e) => onFieldChange(formik, 'tournamentDataStatus', e.value)}
        options={OPTIONS}
      />
    </ConsoleDialog>
  )
}

export default UpdateTournamentDataStatusForm
